'use client';

import { Fragment, type CSSProperties } from 'react';
import { cn } from '@/lib/utils';
import { pad2, type TimeLeft } from '@/lib/bannerCountdown';
import { rem } from './SeasonalLayers';
import styles from './seasonal.module.css';

/**
 * Countdown and affiliate-code boxes of the seasonal pieces, built from the
 * Figma frames of "IQ Capital | New UI 2026":
 *   popup countdown   12770:42336 (desktop, 83.48x86.48 boxes) and
 *                     12840:42817 (mobile, the same boxes at 0.82)
 *   affiliate code    12770:42065 (desktop) / 12770:42104 (mobile)
 *   banner countdown  12940:7028 — the New UI 2026 banner boxes, unchanged
 */

/** Short copy the designs carry next to the countdown / the code. Standard strings, kept in code like the unit labels. */
const COPY: Record<string, { endsIn: string; useCode: string }> = {
  en: { endsIn: 'Ends in', useCode: 'Use this code at checkout' },
  de: { endsIn: 'Endet in', useCode: 'Code beim Checkout einlösen' },
  br: { endsIn: 'Termina em', useCode: 'Use este código no checkout' },
};

export function getSeasonalCopy(language: string | undefined) {
  return COPY[language ?? 'en'] ?? COPY.en;
}

/**
 * Two digits; a digit that changes is remounted so its drop-in plays (handoff
 * "Contagem"). The spans inherit the parent's Blacker/Gestura family
 * explicitly: scss/global/typography.scss gives every <span> Geist.
 */
export function Digits({ value, className }: { value: number; className?: string }) {
  return (
    <span className={cn('tabular-nums', styles.inheritFont, className)}>
      {Array.from(pad2(value)).map((digit, index) => (
        <span key={`${index}-${digit}`} className={cn(styles.digitIn, styles.inheritFont)}>
          {digit}
        </span>
      ))}
    </span>
  );
}

const toUnits = (timeLeft: TimeLeft) => [
  timeLeft.days,
  timeLeft.hours,
  timeLeft.minutes,
  timeLeft.seconds,
];

/* -------------------------------------------------------------------------- */
/*  Popup: four glass boxes with unit labels                                  */
/* -------------------------------------------------------------------------- */

interface SeasonalCountdownBoxesProps {
  timeLeft: TimeLeft;
  labels: [string, string, string, string];
  /** 1 = the desktop box (Figma 12770:42337); the mobile frames use 0.82 (Halloween) and ~0.77 (Black Month). */
  scale?: number;
  className?: string;
}

/**
 * Figma 12770:42337: 12/14 padding, 14 gap, radius 10.431, 1.738 hairline at
 * white 10%, white 6% fill, 26.1 blur; the digit is Blacker Display 44 on a
 * 31px line (cap-height trim), the label Geist 14 at 60% on a 10px line. The
 * digit keeps a 56px min-width so the box does not breathe as the clock runs.
 * Everything is scaled through inline styles, since the mobile frame is the
 * desktop box shrunk rather than a relayout.
 *
 * The hairline is an inset shadow, not a border: Chrome snaps border widths
 * to whole device pixels, so a 1.738px border renders 1px and the box comes
 * out 1.5px smaller than the 83.477x86.477 of the design. The padding carries
 * the hairline's width instead.
 */
export function SeasonalCountdownBoxes({
  timeLeft,
  labels,
  scale = 1,
  className,
}: SeasonalCountdownBoxesProps) {
  const r = (px: number) => rem(px * scale);
  const box: CSSProperties = {
    gap: r(14),
    padding: `${r(14 + 1.738)} ${r(12 + 1.738)}`,
    borderRadius: r(10.431),
    boxShadow: `inset 0 0 0 ${r(1.738)} rgba(255,255,255,0.1)`,
    backdropFilter: `blur(${(26.1 * scale).toFixed(2)}px)`,
  };
  const digit: CSSProperties = {
    fontSize: r(44),
    letterSpacing: r(-1),
    minWidth: r(56),
    lineHeight: 0.705,
  };
  const label: CSSProperties = { fontSize: r(14), letterSpacing: r(-0.42), lineHeight: 0.714 };
  const colon: CSSProperties = { width: r(6.807), height: r(27.517) };
  const units = toUnits(timeLeft);

  return (
    <div
      role="timer"
      aria-label={units.map((unit, index) => `${unit} ${labels[index]}`).join(', ')}
      className={cn('flex items-center', className)}
      style={{ gap: r(6.954) }}
    >
      {units.map((unit, index) => (
        <Fragment key={index}>
          {index > 0 && (
            // 12770:42340 — the ":" is a 6.807x27.517 vector in Figma (the
            // Blacker glyph); the font's colon is narrower, so the export is used.
            // eslint-disable-next-line @next/next/no-img-element -- exported glyph
            <img aria-hidden src="/seasonal/countdown-colon.svg" alt="" className="shrink-0 self-center" style={colon} />
          )}
          <div
            aria-hidden
            className="flex flex-col items-center justify-center bg-white/[0.06] text-white"
            style={box}
          >
            <p className="mb-0 text-center font-blacker font-normal" style={digit}>
              <Digits value={unit} />
            </p>
            <p
              className="mb-0 whitespace-nowrap text-center font-geist font-normal uppercase opacity-60"
              style={label}
            >
              {labels[index]}
            </p>
          </div>
        </Fragment>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Popup: affiliate code box (Black Month)                                   */
/* -------------------------------------------------------------------------- */

/**
 * Figma 12770:42065 (desktop: 40/18 padding, gap 6, Blacker 40 +1px) and
 * 12770:42104 (mobile: 40/12 padding, gap 4, Blacker 32). Same glass as the
 * countdown boxes, radius 6 — and the same inset-shadow hairline, with its
 * 1.738px added to the padding. "YOURCODE" in the design is the placeholder
 * for the partner's own code.
 */
export function SeasonalCodeBox({ code, caption }: { code: string; caption: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-[0.5rem] whitespace-nowrap rounded-[0.75rem] bg-white/[0.06] px-[5.21725rem] py-[1.71725rem] shadow-[inset_0_0_0_0.21725rem_rgba(255,255,255,0.1)] backdrop-blur-[26.1px] lg:gap-[0.75rem] lg:py-[2.46725rem]">
      <p className="mb-0 font-blacker text-[4rem] font-normal leading-[0.9375] tracking-[0.125rem] text-white lg:text-[5rem] lg:leading-[0.925]">
        {code}
      </p>
      <p className="mb-0 font-geist text-[1.75rem] font-normal leading-[1.286] tracking-[-0.0525rem] text-white/70">
        {caption}
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Banner: four small boxes, Gestura digits, no labels                       */
/* -------------------------------------------------------------------------- */

interface SeasonalBannerCountdownProps {
  timeLeft: TimeLeft;
  labels: [string, string, string, string];
  className?: string;
  /** The phone frames draw the same boxes smaller: 0.873 (Halloween), 0.889 (Black Month). */
  scale?: number;
  /**
   * Box height in design px when the frame stretches the boxes beyond their
   * content: the phone frames make them as tall as the pill's row (35.24 /
   * 35.336) with the digits centred.
   */
  boxHeight?: number;
}

/**
 * Figma 13041:6373 / 12940:7006 — the New UI 2026 banner countdown as
 * TopBannerV1 renders it: 5/2 padding, radius 6, white 10% fill and hairline,
 * 9.9 blur, Gestura Headline Bold 20/30 (the Figma file uses the TRIAL cut;
 * the site's Gestura Display Bold stands in, as on the V1 banner), gap 2,
 * the ":" without a box. The phone frames (13047:25250 / 13047:25283) carry
 * the same boxes scaled by 0.873 / 0.889, so every size comes from `scale`.
 * The hairline is an inset shadow with its width added to the padding:
 * Chrome snaps a fractional border to whole pixels.
 */
export function SeasonalBannerCountdown({
  timeLeft,
  labels,
  className,
  scale = 1,
  boxHeight,
}: SeasonalBannerCountdownProps) {
  const r = (px: number) => rem(px * scale);
  const digit: CSSProperties = { fontSize: r(20), lineHeight: r(30), letterSpacing: r(-0.6) };
  const box: CSSProperties = {
    minHeight: boxHeight ? rem(boxHeight) : undefined,
    padding: `${r(2 + 1)} ${r(5 + 1)}`,
    borderRadius: r(6),
    boxShadow: `inset 0 0 0 ${r(1)} rgba(255,255,255,0.1)`,
    backdropFilter: `blur(${(9.9 * scale).toFixed(2)}px)`,
  };
  const units = toUnits(timeLeft);
  return (
    <div
      role="timer"
      aria-label={units.map((unit, index) => `${unit} ${labels[index]}`).join(', ')}
      className={cn('flex shrink-0 items-start whitespace-nowrap', className)}
      style={{ gap: r(2) }}
    >
      {units.map((unit, index) => (
        <span key={index} className="contents">
          {index > 0 && (
            // Figma's ":" (Gestura Headline TRIAL) is 6px wide; the site's cut is
            // narrower, so the glyph is centred in a 6px box to keep the spacing.
            <span
              aria-hidden
              className="inline-block text-center font-gestura font-bold text-white"
              style={{ ...digit, width: r(6) }}
            >
              :
            </span>
          )}
          <span aria-hidden className="flex flex-col items-center justify-center bg-white/10" style={box}>
            <span
              className="text-center font-gestura font-bold text-white"
              style={{ ...digit, minWidth: r(28) }}
            >
              <Digits value={unit} />
            </span>
          </span>
        </span>
      ))}
    </div>
  );
}
